import { openDB, DBSchema, IDBPDatabase } from 'idb';

// Types for offline queue
export interface QueuedMutation {
  id: string;
  type: 'transaction' | 'category' | 'tag' | 'budget' | 'profile';
  action: 'create' | 'update' | 'delete';
  payload: Record<string, unknown>;
  createdAt: string;
  retryCount: number;
}

export interface CachedData {
  key: string;
  data: unknown;
  cachedAt: string;
  expiresAt: string;
}

// Database schema for offline support
interface OfflineDBSchema extends DBSchema {
  mutationQueue: {
    key: string;
    value: QueuedMutation;
    indexes: { 'by-created': string };
  };
  dataCache: {
    key: string;
    value: CachedData;
  };
}

const DB_NAME = 'AxisOfflineDB';
const DB_VERSION = 1;
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

let dbPromise: Promise<IDBPDatabase<OfflineDBSchema>> | null = null;

export function getOfflineDb(): Promise<IDBPDatabase<OfflineDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Mutation queue store
        if (!db.objectStoreNames.contains('mutationQueue')) {
          const queueStore = db.createObjectStore('mutationQueue', { keyPath: 'id' });
          queueStore.createIndex('by-created', 'createdAt');
        }

        // Data cache store
        if (!db.objectStoreNames.contains('dataCache')) {
          db.createObjectStore('dataCache', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

// Queue a mutation for later sync
export async function queueMutation(
  type: QueuedMutation['type'],
  action: QueuedMutation['action'],
  payload: Record<string, unknown>
): Promise<string> {
  const db = await getOfflineDb();
  const id = crypto.randomUUID();
  
  const mutation: QueuedMutation = {
    id,
    type,
    action,
    payload,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  };
  
  await db.put('mutationQueue', mutation);
  
  // Dispatch event so UI can react
  window.dispatchEvent(new CustomEvent('offline-queue-change'));
  
  return id;
}

// Get all pending mutations
export async function getPendingMutations(): Promise<QueuedMutation[]> {
  const db = await getOfflineDb();
  return db.getAllFromIndex('mutationQueue', 'by-created');
}

// Get pending count
export async function getPendingCount(): Promise<number> {
  const db = await getOfflineDb();
  return db.count('mutationQueue');
}

// Remove a mutation after successful sync
export async function removeMutation(id: string): Promise<void> {
  const db = await getOfflineDb();
  await db.delete('mutationQueue', id);
  window.dispatchEvent(new CustomEvent('offline-queue-change'));
}

// Update retry count on failure
export async function incrementRetryCount(id: string): Promise<void> {
  const db = await getOfflineDb();
  const mutation = await db.get('mutationQueue', id);
  if (mutation) {
    mutation.retryCount += 1;
    await db.put('mutationQueue', mutation);
  }
}

// Clear all pending mutations
export async function clearMutationQueue(): Promise<void> {
  const db = await getOfflineDb();
  await db.clear('mutationQueue');
  window.dispatchEvent(new CustomEvent('offline-queue-change'));
}

// Cache data for offline access
export async function cacheData(key: string, data: unknown): Promise<void> {
  const db = await getOfflineDb();
  const now = new Date();
  
  const cached: CachedData = {
    key,
    data,
    cachedAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + CACHE_DURATION_MS).toISOString(),
  };
  
  await db.put('dataCache', cached);
}

// Get cached data
export async function getCachedData<T>(key: string): Promise<T | null> {
  const db = await getOfflineDb();
  const cached = await db.get('dataCache', key);
  
  if (!cached) return null;
  
  // Check if expired
  if (new Date(cached.expiresAt) < new Date()) {
    await db.delete('dataCache', key);
    return null;
  }
  
  return cached.data as T;
}

// Clear expired cache entries
export async function clearExpiredCache(): Promise<void> {
  const db = await getOfflineDb();
  const all = await db.getAll('dataCache');
  const now = new Date();
  
  const tx = db.transaction('dataCache', 'readwrite');
  for (const item of all) {
    if (new Date(item.expiresAt) < now) {
      await tx.store.delete(item.key);
    }
  }
  await tx.done;
}

// Clear all cached data
export async function clearDataCache(): Promise<void> {
  const db = await getOfflineDb();
  await db.clear('dataCache');
}
